<template>
  <button
    class="btn"
    :class="setClassModifiers('btn--')"
    @click="click()"
    :disabled="disabled"
    :type="actionType"
  >
    <span class="btn__inner d-block">
      <slot></slot>
    </span>
    <spinner
      v-if="loading"
      :white="!outline"
      width="2"
    >
    </spinner>
  </button>
</template>

<script>
import setClassModifiers from '@/mixins/setClassModifiers'
import Spinner from '@/ui/elements/Spinner'

export default {
  name: 'v-button',
  components: {
    // vLoader,
    Spinner
  },
  data () {
    return {
      svglink: 'icon-grid'
    }
  },
  mixins: [setClassModifiers],
  props: {
    /**
     * @values large, small, xs-small
     *  */
    size: {
      type: String,
      default: null
    },
    /**
     * @values submit
     */
    actionType: {
      type: String,
      default: null
    },
    /**
     * @values full
     */
    width: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    outline: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    inactive: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: null
    }
  },
  methods: {
    click () {
      /**
      * Click event
      * @property
      */
      this.$emit('click')
    }
  }
}
</script>

<style lang="scss">
  @mixin btnColor($colorName) {
    background-color: $colorName;
    color: #fff;
    border: 1px $colorName solid;

    @media(hover: hover) and (pointer: fine) {
      &:hover {
        background-color: lighten($colorName, 10%);
      }
    }

    &:active {
      background-color: lighten($colorName, 20%);
    }
  }

  @mixin btnColorOutline($colorName) {
    background-color: transparent;
    color: $colorName;
    border: 1px $colorName solid;
  }

  .btn {
    //padding: .9rem 1.5rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    height: 4rem;
    background-color: $color-purple;
    border: 1px $color-purple solid;
    border-radius: $border-radius;
    cursor: pointer;
    transition: all .15s;
    outline: none;
    font-family: inherit;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: $color-white;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: .05rem;
    white-space: nowrap;

    &--rounded {
      padding: 0;
      border-radius: 50%;
      width: 4rem;
    }

    @media(hover: hover) and (pointer: fine) {
      &:hover {
        background-color: lighten($color-primary, 10%);
      }
    }

    &:active {
      background-color: lighten($color-primary, 20%);
      box-shadow: $elevation-down-light;
      transform: translateY(.5px);
    }

    ///////////////////////
    /// LARGE
    &--large {
      //padding: .9rem 4.5rem;
      padding-left: 4.5rem;
      padding-right: 4.5rem;
      height: 4.2rem;
      font-size: 1.3rem;

      // Icons fix
      .btn__icon {
        font-size: 1.5rem;

        &--left {
          margin-right: 1rem;
        }

        &--right {
          margin-left: 1rem;
        }
      }
    }

    &--rounded.btn--large {
      padding: 0;
      width: 4.2rem;;
    }

    &--small {
      //padding: .5rem 1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      height: 2.6rem;
      font-size: 1.2rem;
    }

    &--rounded.btn--small {
      padding: 0;
      width: 2.6rem;
    }

    &--xs-small {
      //padding: .3rem .5rem;
      padding-left: .5rem;
      padding-right: .5rem;
      height: 2.3rem;
      font-size: 1.1rem;
      font-weight: 500;
    }

    &--rounded.btn--xs-small {
      width: 2.3rem;
      padding: 0;
    }

    &--secondary {
      background-color: $color-secondary;
      color: #fff;
      border: 2px lighten($color-secondary, 10%) solid;

      @media(hover: hover) and (pointer: fine) {
        &:hover {
          background-color: lighten($color-secondary, 10%);
        }
      }

      &:active {
        background-color: lighten($color-secondary, 20%);
      }
    }

    &--inactive {

    }

    &--success {
      @include btnColor($color-success)
    }

    &--error {
      @include btnColor($color-error)
    }

    &--grey {
      @include btnColor($color-grey)
    }

    &--blue {
      @include btnColor($color-blue-semantic)
    }

    &--black {
      @include btnColor($color-black)
    }

    &--space {
      @include btnColor($color-space)
    }

    &--outline {
      background-color: transparent;
      color: $color-primary;
      border: 1px $color-primary solid;

      &.btn--secondary {
        @include btnColorOutline($color-secondary)
      }

      &.btn--success {
        @include btnColorOutline($color-success)
      }

      &.btn--error {
        @include btnColorOutline($color-error)
      }

      &.btn--grey {
        @include btnColorOutline($color-grey)
      }

      &.btn--space {
        @include btnColorOutline($color-space)
      }

      @media(hover: hover) and (pointer: fine) {
        &:hover {
          @include btnColorOutline(lighten($color-primary, 10%))
        }
      }

      &:active {
        color: $color-primary;
        border-color: $color-primary;
        background-color: lighten($color-primary, 45%);
        outline: none;
      }
    }

    &--inline {
      border: none;
      padding: 0;
      background-color: transparent;
      display: inline;

      color: currentColor;

      &:hover, &:active {
        background-color: transparent;
      }
    }

    &--full {
      width: 100%;
    }

    &--white {
      background-color: $color-white;
      color: $color-secondary;
      border: 2px $color-white solid;

      &:hover {
        background: $color-white;
        border: 2px lighten($color: $color-primary, $amount: 40%) solid;
      }

      &:active {
        background-color: lighten($color: $color-primary, $amount: 50%);
      }
    }

    &--inactive {
      opacity: .4;
    }

    &:disabled {
      /*color: $color-grey;
      background-color: $color-grey-light;*/
      /*border-color: $color-grey;*/
      opacity: .3;
      cursor: unset;

      &:active {
        transform: translateY(0)
      }
    }
  }
</style>
